import { Box, Grid, Paper, useTheme } from "@mui/material";
import { gridClasses, GridColDef } from "@mui/x-data-grid";
import { FC, useMemo } from "react";
import { Link, Outlet } from "react-router-dom";
import { AgentLocation, Permissions } from "../../api/dtos";
import GridActions from "../../components/GridActions";
import SetLinks from "../../components/layout/SetLinks";
import { StyledButton } from "../../components/StyledButton";
import CollapsibleTablePremium from "../../components/tables/CollapsibleTablePremium";
import { createCustomToolbarProps } from "../../components/utility/CustomToolbarProps";
import AgentProjectSummariesContext from "../../contexts/AgentProjectsContext";
import { DefaultProjectCreateLink } from "../../contexts/layouts/LinkContext";
import useAgentProjectSummaries from "../../hooks/data/useAgentProjectSummaries";

const useAgentLocationColumns = () => {

    const agentColumns = useMemo(() => {
        const columns: GridColDef<AgentLocation>[] =  [
            {
                field: "agentName",
                headerName: "Agent",
                flex: 1
            },
            {
                field: "location",
                headerName: "Location",
                flex: 1
            },
            {
                field: "projectName",
                headerName: "Project",
                flex: 1
            },
            {
                field: "actions",
                headerName: '',
                renderCell: (params) => {
                    return (
                        <GridActions
                            editPermission={Permissions.AdminAccess}
                            editUrl={`/admin/agent-projects/edit/${params.row.id}`}
                        />
                    );
                },
                disableExport: true
            }
        ];

        return columns;
    }, []);

    return agentColumns;
}


const Agents:FC = () => {

    const columns = useAgentLocationColumns();

    const theme = useTheme();

    const context = useAgentProjectSummaries();

    const {data: agentProjects, loading: agentProjectsLoading} = context;
    
    const customToolbarProps = useMemo(() => createCustomToolbarProps({
        fileName: `Agent Projects`,
        exportTitle: `Agent Projects`,
        includeDateTime: true
    }), []);

    return (
        <AgentProjectSummariesContext.Provider value={context}>
            <Outlet />
            <SetLinks
                createProjectLink={DefaultProjectCreateLink}
                stageLinkEnabled={false}
                lotLinkEnabled={false}
            />
            <Grid
                container
                gap={theme.spacing(2)}
                justifyContent={"end"}
                marginBottom={theme.spacing(2)}
            >
                <StyledButton 
                        variant="contained"
                        //@ts-expect-error
                        component={Link}
                        to={`/admin/agent-personnel/create`}
                        state={{
                            back: true
                        }}
                >
                    Add Agent Project
                </StyledButton>
            </Grid>
            <Paper>
                <Box
                    sx={{
                        [`& .${gridClasses.root}`]: {
                            
                            [`& .${gridClasses.columnHeader}`]: {
                                height: 'auto !important',
                                minHeight: 56,
                                
                                [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                                    whiteSpace: 'normal',
                                    
                                    [`& .${gridClasses.columnHeaderTitle}`]: {
                                        whiteSpace: 'normal',
                                        paddingTop: theme.spacing(1.5),
                                        paddingBottom: theme.spacing(1.5) 
                                    }
                                },
                                
                                [`& .${gridClasses.iconButtonContainer}`]: {
                                    width: 'unset',
                                },
                                
                                '&.column-group-header': {
                                    backgroundColor: theme.palette.grey[300],
                                    boxShadow: "1",
                                    textAlign: 'center'
                                }
                            }
                        },
                    }}
                    >
                    <CollapsibleTablePremium
                        density="compact"
                        startCollapsed={false} 
                        title={"Agent Projects"} 
                        loading={agentProjectsLoading} 
                        columns={columns}
                        rows={agentProjects}
                        disableColumnMenu={false}
                        disableColumnFilter={false}
                        {...customToolbarProps}
                    />
                </Box>
            </Paper>
        </AgentProjectSummariesContext.Provider>
    );
}

export default Agents;